import {
  HStack,
  IconButton,
  Stack,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Icon } from "../../components/Icon";
import { useStores } from "../../hooks/useStores";
import { useCallback } from "react";
import { PrimayButton } from "../../components/Button";
import ReactGA from "react-ga";
import ReactPixel from "react-facebook-pixel";
import { Step, Steps, useSteps } from "chakra-ui-steps";
import { SupervisionedWorkoutsStep1 } from "./SupervisionedWorkoutsStep1";
import { SupervisionedWorkoutsStep2 } from "./SupervisionedWorkoutsStep2";
import { SupervisionedWorkoutsStep3 } from "./SupervisionedWorkoutsStep3";
import dayjs from "dayjs";

export const CustomerLongLiveTraining: React.FC = observer(() => {
  const { session } = useStores();
  const { t } = useTranslation();
  const history = useHistory();
  const [sessionId, setSessionId] = useState("");
  const { setStep, activeStep } = useSteps({
    initialStep: 0,
  });

  const dailyTimetable = useMemo(() => {
    const currentDayOfWeek = new Date().getDay();
    return session.supervisionedWorkoutTimetable &&
      session.supervisionedWorkoutTimetable[currentDayOfWeek] &&
      (session.supervisionedWorkoutTimetable[currentDayOfWeek].slot1.from ||
        session.supervisionedWorkoutTimetable[currentDayOfWeek].slot2.from)
      ? session.supervisionedWorkoutTimetable[currentDayOfWeek]
      : null;
  }, [session.supervisionedWorkoutTimetable]);

  const isSessionAvailable = useMemo(() => {
    let result = false;

    if (dailyTimetable) {
      const today = new Date();

      let isSlot1Available = false,
        isSlot2Avaialbe = false;

      if (dailyTimetable.slot1.from && dailyTimetable.slot1.to) {
        const slot1From = new Date(dailyTimetable.slot1.from),
          slot1To = new Date(dailyTimetable.slot1.to);

        // update date, month, year because from and to store the time info
        slot1From.setMonth(today.getMonth());
        slot1From.setFullYear(today.getFullYear());
        slot1From.setDate(today.getDate());
        slot1From.setSeconds(0);

        slot1To.setMonth(today.getMonth());
        slot1To.setFullYear(today.getFullYear());
        slot1To.setDate(today.getDate());
        slot1To.setSeconds(0);

        if (
          today.getTime() >= slot1From.getTime() &&
          today.getTime() <= slot1To.getTime()
        ) {
          isSlot1Available = true;
        }
      }

      if (dailyTimetable.slot2.from && dailyTimetable.slot2.to) {
        const slot2From = new Date(dailyTimetable.slot2.from),
          slot2To = new Date(dailyTimetable.slot2.to);

        // update date, month, year because from and to store the time info
        slot2From.setMonth(today.getMonth());
        slot2From.setFullYear(today.getFullYear());
        slot2From.setDate(today.getDate());
        slot2From.setSeconds(0);

        slot2To.setMonth(today.getMonth());
        slot2To.setFullYear(today.getFullYear());
        slot2To.setDate(today.getDate());
        slot2To.setSeconds(0);

        if (
          today.getTime() >= slot2From.getTime() &&
          today.getTime() <= slot2To.getTime()
        ) {
          isSlot1Available = true;
        }
      }

      result = isSlot1Available || isSlot2Avaialbe;
    }

    return result;
  }, [dailyTimetable]);

  const handleStart = useCallback(() => {
    const _sId = `${
      session.user?.id
    }_CLLT_${new Date().getFullYear()}${new Date().getMonth()}${new Date().getDate()}`;
    setSessionId(_sId);

    session.traceActivity({
      externalId: _sId,
      dateTime: new Date(),
      type: "long-live-traning",
      eventMeta: {
        status: "started",
      },
    });

    ReactGA.event({
      category: "Customer",
      action: "Allenamento Diretta Lunga",
    });
    ReactPixel.track("Allenamento Diretta Lunga");
  }, [session]);

  const handleEnd = useCallback(() => {
    session.traceActivity({
      externalId: sessionId,
      dateTime: new Date(),
      type: "long-live-traning",
      eventMeta: {
        status: "end",
      },
    });
    history.push("/client/board");
  }, [history, session, sessionId]);

  const handleGoToStep = (step: number) => {
    switch (step) {
      case 1:
        setStep(step);
        break;
      case 2:
        setStep(step);
        break;
    }
  };

  return (
    <Stack margin={"2rem"} gridGap={"2rem"}>
      <HStack marginBottom={"0.5rem"}>
        <IconButton
          aria-label="Back"
          icon={<Icon iconName={"HiArrowLeft"} />}
          variant={"ghost"}
          onClick={() => history.push("/client/board")}
        />
        <Stack>
          <Text fontSize="3xl">
            {t("screens.customerLongLiveTraining.title")}
          </Text>
          <Text fontSize="xl" fontWeight={300}>
            {t("screens.customerLongLiveTraining.subtitle")}
          </Text>
        </Stack>
      </HStack>

      {dailyTimetable && (
        <Stack justifyContent={"center"} gridGap={"1rem"} flexWrap={"wrap"}>
          <Table size="sm" paddingBottom={"2rem"}>
            <Thead>
              <Tr>
                <Th>Orari</Th>
                {dailyTimetable.slot1.from && (
                  <Th>
                    {t("screens.adminSettings.supervisionedWorkouts.slot1")}
                  </Th>
                )}
                {dailyTimetable.slot2.from && (
                  <Th>
                    {t("screens.adminSettings.supervisionedWorkouts.slot2")}
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>{t(`dayOfWeek.${new Date().getDay()}`)}</Td>
                {dailyTimetable.slot1.from && (
                  <Td>
                    {dayjs(dailyTimetable.slot1.from).format("HH:mm")}-
                    {dayjs(dailyTimetable.slot1.to).format("HH:mm")}
                  </Td>
                )}
                {dailyTimetable.slot2.from && (
                  <Td>
                    {dayjs(dailyTimetable.slot2.from).format("HH:mm")}-
                    {dayjs(dailyTimetable.slot2.to).format("HH:mm")}
                  </Td>
                )}
              </Tr>
            </Tbody>
          </Table>
        </Stack>
      )}

      {isSessionAvailable ? (
        <Stack justifyContent={"center"} gridGap={"1rem"} flexWrap={"wrap"}>
          <Steps activeStep={activeStep}>
            <Step label={"Riscaldamento"}>
              <SupervisionedWorkoutsStep1
                onCompleted={() => handleGoToStep(1)}
                onStart={handleStart}
              />
            </Step>
            <Step label={"Cardio e Forza"}>
              <SupervisionedWorkoutsStep2 next={() => handleGoToStep(2)} />
            </Step>
            <Step label={"Stretching"}>
              <SupervisionedWorkoutsStep3 onCompleted={() => null} />
            </Step>
          </Steps>
          <Flex width="100%" justify="flex-end">
            {activeStep === 1 && (
              <PrimayButton onClick={() => handleGoToStep(2)}>
                Passa alla sessione di stretching
              </PrimayButton>
            )}
            {activeStep === 2 && (
              <PrimayButton onClick={handleEnd}>Fine allenamento</PrimayButton>
            )}
          </Flex>
        </Stack>
      ) : (
        <Stack
          justifyContent={"center"}
          gridGap={"1rem"}
          flexWrap={"wrap"}
          alignContent="center"
        >
          <Text>Nessun allenamento in corso</Text>
        </Stack>
      )}
    </Stack>
  );
});
